<template>
    <v-card
        id="editor-settings"
        elevation="1"
        :class="cardClass"
        :loading="loading"
        @click.passive="setActive()"
    >
        <expansion-panel-header v-if="editPrivilege && !inner_properties.hidden"
            :tooltipText="tooltipText"
            :collapsable="collapsable"
            :collapsed="settingsCollapsed"
            :loading="loading"
            :icon="expandIcon"
            @click="settingsCollapsed = !settingsCollapsed"
        >
            {{ t_settings_of + t_title() }}
        </expansion-panel-header>

        <div v-show="!settingsCollapsed || grid==='3cols'">
            <div class="placeholder"/>

            <div ref="scrollBox" v-if="editPrivilege && inner_properties.hidden === false" class="scrollBox" :style="heightStyle" @scroll="scrolled">

                <settings-container v-if="sectionAvailable('widget')"
                                    :title="t_widget_settings"
                                    initially-open>
                    <widget-settings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :element_nr="value"
                        :loading="loading"
                        :readonly="readonly"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        :widget_settings="widget_settings[inner_properties.id]"
                        :widget_properties="widget_properties[inner_properties.id]"
                        v-on:initToBeScrolled="initToBeScrolled($event['type'], $event['id'])"
                        v-on:leaveElementToBeScrolled="leaveElementToBeScrolled()">
                    </widget-settings>
                </settings-container>

                <div class="noCollapse">
                    <div v-if="creator === false" class="alert alert-danger">
                        <div>{{ t_creator_invalid }}</div>
                        <template v-for="element in creator_errors">
                            <template v-if="element && element.hidden === false && element.errors.length > 0">
                                <div>{{ element.name }}</div>
                                <ul>
                                    <li v-for="error in element.errors">
                                        {{ error }}
                                    </li>
                                </ul>
                            </template>
                        </template>
                    </div>

                    <common-no-collapse-settings
                        v-if="sectionAvailable('no_collapse')"
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :readonly="readonly"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        :all_layer_layers="all_layer_layers"
                        :parent_layers="[active_layer.id]"
                        :esign-connection-direct="esignConnectionDirect"
                        v-on:initToBeScrolled="initToBeScrolled($event['type'], $event['id'])"
                        v-on:leaveElementToBeScrolled="leaveElementToBeScrolled()">

                        <div class="row" v-if="propertyAvailable('active_layer')">
                            <v-list dense
                                    style="width: 100%">
                                <v-list-item-group
                                    v-model="active_layer_index"
                                    color="primary">
                                    <v-list-item
                                        v-for="(layer, layerkey) in active_layers"
                                        :key="layerkey">
                                        <div style="display: flex; align-items: center; gap: 8px; padding-block: 8px">
                                            <element-state :state="layer.state" size="small" colored-shadow/>
                                            {{layer.text}}
                                        </div>
                                    </v-list-item>
                                </v-list-item-group>
                            </v-list>
                        </div>

                        <div class="row" v-if="propertyAvailable('resolution')">
                            <div class="col-md-12">
                                {{ t_resolution }}:
                                <v-chip small
                                        label
                                        outlined>
                                    {{ active_layer.width }}x{{ active_layer.height }}
                                </v-chip>
                            </div>
                        </div>
                        <div class="row" v-if="propertyAvailable('elements_count')">
                            <div class="col-md-12">
                                {{t_approved_elements}} / {{ t_total_elements }}:
                                <v-chip small
                                        label
                                        outlined>
                                    {{ approved_elements }} / {{ total_elements }}
                                </v-chip>
                            </div>
                        </div>
                    </common-no-collapse-settings>
                </div>

                <settings-container v-if="sectionAvailable('font')"
                                    :title="t_font">
                    <font-settings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :readonly="readonly"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        v-on:initToBeScrolled="initToBeScrolled($event['type'], $event['id'])"
                        v-on:leaveElementToBeScrolled="leaveElementToBeScrolled()">
                    </font-settings>
                </settings-container>

                <settings-container v-if="sectionAvailable('extended')"
                                    :title="t_extended_properties">
                    <extended-settings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :readonly="readonly"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        v-on:initToBeScrolled="initToBeScrolled($event['type'], $event['id'])"
                        v-on:leaveElementToBeScrolled="leaveElementToBeScrolled()">
                    </extended-settings>
                </settings-container>

                <settings-container v-if="sectionAvailable('playback')"
                                    :title="t_playback_properties">
                    <playbackSettings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :readonly="readonly"
                        :is_parent="elementkey===0"
                        is_publish="1"
                        :playback-privilege="playbackPrivilege"
                        :date-privilege="inner_properties && (
                            (!['picture', 'video', 'pdf', 'ppt', 'template', 'campaign'].includes(inner_properties.type)) ||
                            (inner_properties.type === 'template' && editPrivilegeTemplate) ||
                            (inner_properties.type === 'campaign' && editPrivilegeCampaign) ||
                            (['picture', 'video', 'pdf', 'ppt'].includes(inner_properties.type) && editPrivilegeMediapool))">
                    </playbackSettings>
                </settings-container>

                <settings-container v-if="sectionAvailable('user')"
                                    :title="t_plays_on">
                    <div ref="userCollapseContent">
                        <div class="row" v-if="propertyAvailable('screens')">
                            <div id="select-screens" class="col-md-12">
                                <v-autocomplete
                                    v-model="inner_screens"
                                    :label="t_screens"
                                    :disabled="readonly"
                                    :items="screensData.screens"
                                    multiple
                                    attach="#select-screens .v-select"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </v-autocomplete>
                            </div>
                        </div>
                        <div class="row" v-if="propertyAvailable('screengroups')">
                            <div id="select-screengroups" class="col-md-12">
                                <v-autocomplete
                                    v-model="inner_screengroups"
                                    :label="t_screengroups"
                                    :disabled="readonly"
                                    :items="selectable_screengroups"
                                    multiple
                                    attach="#select-screengroups .v-select"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </v-autocomplete>
                            </div>
                        </div>
                    </div>
                </settings-container>
            </div>
        </div>

        <div
            id="screens-modal"
            class="modal inmodal fade"
            role="dialog"
            aria-hidden="true"
            data-backdrop="static"
            tabindex="-1"
        >
            <div class="modal-dialog">
                <div class="modal-content animated fadeInUp">
                    <div class="modal-header-dialog-painting">
                        <h2 class="modal-title">{{ t_screen_settings }}</h2>
                    </div>
                    <div class="modal-body">
                        <div class="row m-b" v-if="propertyAvailable('screens')">
                            <div id="modal-select-screens" class="col-md-12">
                                <v-autocomplete
                                    v-model="inner_screens"
                                    :label="t_screens"
                                    :disabled="readonly"
                                    :items="screensData.screens"
                                    multiple
                                    attach="#modal-select-screens .v-select"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </v-autocomplete>
                            </div>
                        </div>
                        <div class="row"
                             v-if="propertyAvailable('screengroups')">
                            <div id="modal-select-screengroups" class="col-md-12">
                                <v-autocomplete
                                    v-model="inner_screengroups"
                                    :label="t_screengroups"
                                    :disabled="readonly"
                                    :items="selectable_screengroups"
                                    multiple
                                    attach="#modal-select-screengroups .v-select"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </v-autocomplete>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <v-btn type="button" class="btn btn-primary" data-dismiss="modal"
                                @click="closeScreensModal()">
                            {{ t_close }}
                        </v-btn>
                    </div>
                </div>
            </div>
        </div>
    </v-card>
</template>

<script>
    import utility from '../../../utility';
    import commonNoCollapseSettings from "../settings/commonNoCollapseSettings";
    import fontSettings from "../settings/fontSettings";
    import extendedSettings from "../settings/extendedSettings";
    import commonSettingsMixin from "../settings/commonSettingsMixin";
    import playbackSettings from "../settings/playbackSettings";
    import elementState from './elementstate.vue';
    import widgetSettings from "../settings/widgetSettings";
    import elementTypes from "../elements/types";
    import PublishTooltipButton from "../common/publish-tooltip-button.vue";
    import SettingsContainer from "./settings-container.vue";
    import ExpansionPanelHeader from "../common/expansion-panel-header.vue";
    import {TIMEOUT_DURATION} from "../common/HeightCalculator";

    export default {
        components: {
            ExpansionPanelHeader,
            SettingsContainer,
            PublishTooltipButton,
            commonNoCollapseSettings,
            playbackSettings,
            fontSettings,
            extendedSettings,
            widgetSettings,
            elementState
        },
        mixins: [
            commonSettingsMixin,
        ],
        props: {
            value: Number,
            grid: String,
            mode: String,
            startCollapsed: Boolean,
            startActive: Boolean,
            active_layer: [Object, Array],
            /** @type {PublishLayer_Active[]} */
            active_layers: Array,
            /** @type {PublishLayer_All[]} */
            all_layer_layers: Array,
            /** @type {'1' | undefined} */
            editPrivilege: String,
            playbackPrivilege: Boolean,
            esignConnectionDirect: String,
            /** @type {'1' | undefined} */
            editPrivilegeMediapool: String,
            /** @type {'1' | undefined} */
            editPrivilegeTemplate: String,
            /** @type {'1' | undefined} */
            editPrivilegeCampaign: String,
            /** @type {ScreensData} */
            screensData: Object,
            expandingUp: Boolean
        },
        data: function () {
            return {
                t_close: Lang.langGet("Close"),
                t_resolution: Lang.langGet("Resolution"),
                t_approved_elements: Lang.langGet("Approved elements"),
                t_total_elements: Lang.langGet("Total elements"),
                t_duration: Lang.langGet("Duration"),
                t_m: Lang.langGet("m"),
                t_s: Lang.langGet("s"),
                t_screens: Lang.langChoice("Screen|Screens", 2),
                t_screengroups: Lang.langChoice("Screengroup|Screengroups", 2),
                t_screen_settings: Lang.langGet("Plays on"),
                t_my_screens: Lang.langGet("My Screens"),
                t_creator_invalid: Lang.langGet("Element cannot be saved:"),
                inner_element: {
                    id: 0,
                    layer_id: -1,
                    data: {
                        type: 'Layer',
                        name: '',
                        hidden: false,
                    },
                },
                empty_element: {
                    id: 0,
                    layer_id: -1,
                    data: {
                        type: 'Layer',
                        name: '',
                        hidden: false,
                    },
                },
                inner_properties: {
                    type: 'Layer',
                    name: '',
                    hidden: false,
                },
                prev_inner_properties: null,
                creator: true,
                saving: false,
                scrollingScreen: false,

                oldValue: -1,
                opened_panels: [],

                basicPrivilege: true,
                advancedPrivilege: true,

                visibleElements: [],

                heightStyle: undefined,

                lastSelectedId: undefined
            }
        },
        computed: {
            cardClass() {
                return 'float-e-margins no-margin publish fixed-height';
            },
            elementkey: function() {
                return this.inner_element.id;
            },
            default_duration: function () {
                return Laravel.default_duration;
            },
            approved_elements: function () {
                let number = 0;
                if(this.active_layer) {
                    _.forEach(this.visibleElements, function (element) {
                        if(element.data.hidden === false) {
                            if (element.data.approved === true) {
                                number++;
                            }
                        }
                    });
                }
                return number;
            },
            total_elements: function () {
                let number = 0;
                if(this.active_layer && this.visibleElements) {
                    _.forEach(this.visibleElements, function (element) {
                        if (element.data.hidden === false) {
                            number ++;
                        }
                    });
                }
                return number;
            },
            selectable_screengroups: function () {
                return this.screensData.screengroups.filter((screengroup) => {
                    return screengroup.text !== this.t_my_screens+'LLL';
                })
            },
            inner_screens: {
                get () {
                    return this.inner_properties.screens
                },
                set (val) {
                    this.inner_properties.screens = val;
                    EaseEventBus.fire('updateElementState', this.value);
                }
            },
            inner_screengroups: {
                get () {
                    return this.inner_properties.screengroups
                },
                set (val) {
                    this.inner_properties.screengroups = val;
                    EaseEventBus.fire('updateElementState', this.value);
                }
            },
            active_layer_index: {
                get() {
                    return this.active_layer?.id != null ? this.active_layers?.findIndex(layer => layer.id === this.active_layer.id) : null
                },
                set(val) {
                    // we are using v-list-item-group which has the option to force one item to be selected
                    // but with our implementation it is not possible to use this feature
                    // so if the value is undefined (=> no item is selected, happens if you choose the currently selected item)
                    // just return and act as if the current item is still selected
                    if (val == undefined) {
                        return;
                    }
                    this.changeLayer(val)
                }
            },
            collapsable() {
                return !this.loading && this.grid === '2cols'
            },
            tooltipText() {
                return `${this.settingsCollapsed ? this.t_expand : this.t_collapse} ${this.t_title()}`
            },
            expandIcon() {
                return this.settingsCollapsed
                    ? (this.expandingUp ? 'fas fa-chevrons-up' : 'fas fa-chevrons-down')
                    : (this.expandingUp ? 'fas fa-chevrons-down' : 'fas fa-chevrons-up');
            }
        },
        watch: {
            mode() {
                this.timeoutHeightRecalculation();
            },
            grid(newVal) {
                if (newVal === '2cols') this.settingsCollapsed = false;

                this.timeoutHeightRecalculation();
            },
            settingsCollapsed() {
                this.timeoutHeightRecalculation();
                EaseEventBus.fire('WORKAROUND_recalculate_dragdrop_height');
            },
            active_layer: {
                handler: function() {
                    if(this.active_layer && this.visibleElements[this.value]) {
                        if(this.active_layer.id !== this.inner_element.layer_id) {
                            this.inner_element = this.visibleElements[this.value];
                        }
                    } else {
                        this.inner_element = this.empty_element;
                    }

                    this.updateInnerProperties()

                    this.validateAll();
                },
                deep: true
            },
            value: function() {
                if(this.active_layer && this.visibleElements[this.value]) {
                    this.inner_element = this.visibleElements[this.value];
                } else {
                    this.inner_element = this.empty_element;
                }

                this.opened_panels = [];
                this.$nextTick(() => {
                    this.updateInnerProperties();
                });
            },
            inner_element: function (element) {
                const elementTypes = ['text', 'iframe', 'emptyrectangle', 'layer', 'esign', 'rssfeed', 'streaming'];
                if (!elementTypes.includes(element.data.type)) {
                    this.prev_inner_properties = null;
                }
                this.old_text_direction = null;
            },
            visibleElements(elements) {
                if (!elements.find(el => el.id === this.lastSelectedId)) {
                    EaseEventBus.fire('changeActiveElementIndex', -1);
                }
            },
            inner_properties: {
                handler: function() {
                    this.autoUpdateTextAlignment();

                    this.validate(this.value);

                    if(this.inner_element && this.inner_element.valid_from !== undefined) {
                        let valid_from = this.inner_element.valid_from;
                        let valid_to = this.inner_element.valid_to;

                        this.visual_validity = utility.checkValidity(valid_from, valid_to);
                    } else {
                        this.visual_validity = '';
                    }

                    if(this.innerPropertiesChanged()) {
                        this.lastSelectedId = this.inner_element.id;
                        EaseEventBus.fire("elementChanged", this.inner_element.id);
                    }

                    if (this.inner_properties.type === 'Layer' && this.inner_properties.name === '') {
                        this.closeScreensModal();

                        EaseEventBus.fire('changeActiveElementIndex', -1);
                    }
                },
                deep: true
            },
            startActive: function() {
                this.active = this.startActive;
            },
        },
        methods: {
            autoUpdateTextAlignment: function () {
                if(this.old_text_direction !== null) {
                    if (this.old_text_direction !== this.inner_properties.text_direction) {
                        if (this.inner_properties.text_direction === 'rtl' && this.inner_properties.text_align === 'left') {
                            this.inner_properties.text_align = 'right';
                        }

                        if (this.inner_properties.text_direction === 'ltr' && this.inner_properties.text_align === 'right') {
                            this.inner_properties.text_align = 'left';
                        }
                    }
                }
                this.old_text_direction = this.inner_properties.text_direction;
            },
            stabilizeNumericValue(value) {
                if(!isNaN(value) && value !== '' && value !== null) {
                    return Math.round(value * 100000) / 100000;
                }

                return value;
            },
            getValue(properties, propertyName, definition) {
                const get = () => {
                    if (definition.computed) {
                        return _.get(properties, definition.computed);
                    }
                    return properties[propertyName];
                }

                const value = get();
                return this.stabilizeNumericValue(value);
            },
            selectedElementIndexChanged() {
                return this.value !== this.oldValue;
            },
            innerPropertiesChanged: function () {
                let changes = false;

                if(this.prev_inner_properties && !this.selectedElementIndexChanged()) {
                    for (const [propertyName, definition] of Object.entries(this.elementProperties())) {
                        const value = this.getValue(this.inner_properties, propertyName, definition);
                        const previousValue = this.getValue(this.prev_inner_properties, propertyName, definition);

                        if(JSON.stringify(value) === JSON.stringify(previousValue)) continue;

                        if(previousValue || value !== this.defaultValue(this.inner_properties.type, propertyName)) {
                            changes = true;
                            break;
                        }
                    }
                }

                this.prev_inner_properties = JSON.parse(JSON.stringify(this.inner_properties));
                this.oldValue = this.value;

                return changes;
            },
            updateInnerProperties: function () {
                if (this.inner_element !== null) {
                    let new_properties = this.inner_element.data;
                    new_properties.index = this.value;
                    this.inner_properties = new_properties;

                    this.loadDefinition();
                    this.updateInnerVariables();
                }
            },
            updateInnerVariables: function() {
                for (const [property, def] of Object.entries(this.elementProperties())) {
                    let dataProp = 'inner_' + property;

                    if(dataProp in this) {
                        if (def.computed) {
                            this[dataProp] = _.get(this.inner_properties, def.computed);
                        } else {
                            this[dataProp] = this.inner_properties[property];
                        }
                    }
                }
            },
            t_title: function (id=null) {
                let element = null;
                if(id === null) {
                    element = this.inner_properties;
                }
                else {
                    if(this.visibleElements[id]) {
                        element = this.visibleElements[id].data;
                    } else if(id === -1) {
                        this.inner_element = this.empty_element;
                        this.inner_properties = this.inner_element.data;
                        element = this.inner_properties;
                    }
                }
                if(!element) {
                    return "";
                }

                let type = element.type;
                let name = element.name;

                if(type === 'Layer') {
                    name = this.active_layer.fullname;
                } else {
                    if(type !== '') {
                        if(type === 'campaign' && element.dynamic) {
                            type = Lang.langGet("Dynamic Campaign");
                        } else {
                            type = Lang.langGet(elementTypes.titles[type]);
                        }
                    }
                }

                if(name !== '' && name !== null) {
                    name = ' "' + name + '"';
                } else {
                    name = "";
                }

                return Lang.langGet(':name', {name: type + name});
            },
            validate: function(id, fromValidateAll=false) {
                let validationResult = false;

                let element = null;
                if(id === null) {
                    element = this.inner_properties;
                }
                else {
                    if(this.visibleElements[id]) {
                        element = this.visibleElements[id].data;
                    } else if(id === -1) {
                        //Nothing to validate for layer
                    }
                }
                if (element === null) {
                    return;
                }

                let toBeValidated = {
                    'valid-from': element.valid_from,
                    'valid-to': element.valid_to,
                };
                validationResult = utility.validate(toBeValidated);
                validationResult = this.validateNumberProperties(element, validationResult, "p_");


                Vue.set(this.creator_errors, id, {
                    'name':  this.t_title(id),
                    'errors': validationResult.errors,
                    'hidden': element.hidden,
                });
                this.$emit('validation', this.creator_errors);

                if(!fromValidateAll) {
                    this.setValidState();
                }
            },
            setValidState() {
                let valid = true;
                if(valid) {
                    for(let element of this.creator_errors) {
                        if(element && element.hidden === false && element.errors.length > 0) {
                            valid = false;
                            break;
                        }
                    }
                }
                this.creator = valid;
            },
            validateAll() {
                this.creator_errors = [];
                this.validate(-1);
                for (let id of this.visibleElements.keys()) {
                    this.validate(id, true);
                }
                this.setValidState();
            },
            closeScreensModal: function () {
                EaseEventBus.fire('hideModal', 'screens-modal');
            },

            loadDefinition: function () {
                let type = '';
                if (this.elementkey === 0) {
                    type = 'base';
                } else {
                    type = this.inner_properties.type;
                }
                this.loadDefinitionForType(type, "p_");
            },
            scrollToScreenSettings: function() {
                EaseEventBus.fire('settingsActive');
                this.$nextTick(function() {
                    this.scrollingScreen = true;
                    EaseEventBus.fire('toggleCollapse', {'name': 'User', 'collapsed': false});
                });
            },
            scrollToScreenSettingsPart2: function() {
                if(this.scrollingScreen) {
                    this.$nextTick(function () {
                        if (this.$refs.scrollBox && this.$refs.userCollapseContent) {
                            let scrollY = this.$refs.userCollapseContent.offsetTop - this.$refs.scrollBox.offsetTop;
                            this.$refs.scrollBox.scrollTo(0, scrollY);
                            this.scrollingScreen = false;
                        }
                    });
                }
            },
            changeLayer: function(layerkey) {
                EaseEventBus.fire('changeLayer', layerkey);
            },
            onTitleClick() {
                if(!this.isTitleClickable) return
                this.settingsCollapsed = !this.settingsCollapsed
            },
            timeoutHeightRecalculation(forceRecalculation) {
                setTimeout(() => {
                    this.heightStyle = this.settingsStyle(forceRecalculation);
                }, TIMEOUT_DURATION);
            },
        },
        mounted() {
            this.loadDefinition();

            this.settingsCollapsed = this.startCollapsed;

            EaseEventBus.listen('loadedLayer', () => {
                this.validateAll();
            });

            EaseEventBus.listen('scrollToScreenSettings', () => {
                this.scrollToScreenSettings();
            });
            EaseEventBus.listen('toggleCollapseFinished', () => {
                if (this.scrollingScreen === true) {
                    this.scrollToScreenSettingsPart2();
                }
            });
            EaseEventBus.listen('updateVisibleElements', elements => {
                this.visibleElements = elements;
            })
            EaseEventBus.listen('collapseSettings', () => {
                this.settingsCollapsed = true;
            });
            EaseEventBus.listen('expandSettings', () => {
                this.settingsCollapsed = false;
            });

            EaseEventBus.listen('WORKAROUND_recalculate_settings_height', () => {
                this.timeoutHeightRecalculation();
            });

            EaseEventBus.listen('windowResize', (event) => {
                this.timeoutHeightRecalculation(true);
            });

            EaseEventBus.listen('activeElementDataLinkedFileChanged', (elements) => {
                this.inner_properties.linked_files = elements;
            });

            this.timeoutHeightRecalculation();
        }
    }
</script>
